import { Colors } from "@/enums/Colors";
import GenericForm from "@/external/ensolvers-core-frontend-mui-v5/components/Common/GenericForm";
import { SelectInput } from "@/external/ensolvers-core-frontend-mui-v5/components/FormInputs/SelectInput";
import { TextFieldInput } from "@/external/ensolvers-core-frontend-mui-v5/components/FormInputs/TextFieldInput";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { CompanyOnboarding } from "@/interfaces/onboarding-interfaces/CompanyOnboarding";
import OnboardingService from "@/services/OnboardingService";
import CountriesService, { CountryData, StateData } from "@/services/countries/CountriesService";
import { getFormattedUtmParams } from "@/utils/UtmParams";
import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { Button, Divider, InputAdornment, Skeleton, Typography, styled, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import ReactGA from "react-ga4";
import { Control, Controller, FieldValues, Resolver } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import SignUpQuestionLogo from "../../../assets/images/sign-up-question-logo.svg";
import LazoSelectInput from "../common/form/LazoSelectInput";
import { getStatesByCountry } from "../utils/Countries/CountriesStaticStatesDataUtils";
import LazoPurpleBackground from "../utils/LazoPurpleBackground";
import { ContinueButton } from "./ContinueButton";
import HeaderOnBoardingComponent from "./HeaderOnBoardingComponent";
import { Container, Text, textFieldStyles } from "./LazoOnboarding";
import { OboardingStepsComponent } from "./OboardingStepsComponent";
import { OnboardingCheckbox } from "./OnboardingCheckbox";

export const YEARS: number[] = Array.from({ length: 20 }, (_, index) => new Date().getFullYear() - index);

export const USA_STATES: string[] = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

export const INDUSTRIES = [
  "Agriculture and Agricultural Technology",
  "Artificial Intelligence",
  "Biotechnology",
  "Blockchain and Cryptocurrencies",
  "Cybersecurity",
  "eCommerce",
  "EdTech",
  "Fashion and Retail",
  "Fintech",
  "Food Industry and Food Technology",
  "Health and Wellness",
  "HR Industry & HR Tech",
  "Insurtech",
  "Internet of Things",
  "Logistics and Supply Chain",
  "Marketing and Digital Advertising",
  "Marketplace",
  "Media & Entertainment",
  "Product",
  "Professional Services and Consulting",
  "Renewable Energy",
  "Social Impact and Sustainability",
  "Software as a Service",
  "Software Factory",
  "Transportation and Mobility",
  "Travel and Tourism",
  "Virtual and Augmented Reality",
  "Individual",
  "Venture Capital and Entrepreneurship",
  "Real State & PropTech",
  "Cloud Services",
  "Others",
];

export const CATEGORIES = [
  "Pre-seed",
  "Seed",
  "Angel",
  "Private Equity",
  "Debt Financing",
  "Convertible Note",
  "Grant",
  "Bootstrapped",
  "Corporate Round",
  "Equity Crowdfunding",
  "Product Crowdfunding",
  "Secondary Market",
  "Post-IPO Equity",
];

const letters = Array.from({ length: 10 }, (_, index) => String.fromCharCode(65 + index));
export const SERIES_CATEGORIES = letters.map((letter) => `Series ${letter}`);
export const FINAL_CATEGORIES = [...CATEGORIES, ...SERIES_CATEGORIES];
export const NUMBER_OF_EMPLOYESS: string[] = ["2-10", "11-50", "51-200", "201-500", "501-1000", "+1000"];

export const StyledImage = styled("img")({
  "&.sign-up-info": {
    display: "none",
    position: "absolute",
    top: "90%",
    right: "2%",
    cursor: "pointer",
    zIndex: 1,
  },
});

interface OnboardingCompanyFormProps {
  companyInformation: CompanyOnboarding;
  handleContinue: (companyInformation?: CompanyOnboarding) => void;
  setCompanyInformation: (companyInformation: CompanyOnboarding) => void;
  buttonColor?: Colors | string;
  addCompany?: boolean;
}

export function OnboardingCompanyForm({ handleContinue, setCompanyInformation, buttonColor, addCompany }: OnboardingCompanyFormProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [countries, setCountries] = useState<CountryData[]>([]);
  const [countryNames, setCountryNames] = useState<string[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<CountryData>();
  const [states, setStates] = useState<StateData[]>([]);
  const [statesNames, setStatesNames] = useState<string[]>([]);
  const [loadingStates, setLoadingStates] = useState<boolean>(false);
  const [isLoadingCountry, setIsLoadingCountry] = useState(false);

  const schema = Yup.object()
    .shape({
      companyName: Yup.string().required("Company name is required"),
      legalName: Yup.string().required("Company legal name is required"),
      url: Yup.string().required("URL is required"),
      telephone: Yup.number().typeError("Telephone must be a number"),
      // .required("Telephone is required"),
      description: Yup.string().required("Description is required").max(250, "Description should have max of 250 characters"),
      //  country: Yup.string().required("Country is required"),
      streetAddress: Yup.string().required("Street address is required"),
      city: Yup.string().required("City is required"),
      state: Yup.string().required("State is required"),
      zipCode: Yup.string().required("Zip code is required"),
      industry: Yup.string().required("Industry is required"),
      yearFounded: Yup.number().required("Year found is required"),
      startupStage: Yup.string().required("Latest financial stage is required"),
      numberOfEmployees: Yup.string().required("Number of employees is required"),
      //keyInvestors: Yup.string().required("Key investors is required"),
      ebitda: Yup.number().typeError("EBITDA muste be a number").required("EBITDA is required"),
      cashOnHand: Yup.number().typeError("Cash on hand must be a number").required("Cash on hand is required"),
      monthlyRevenue: Yup.number().typeError("Monthly revenue must be a number").required("Monthly revenue is required"),
      totalFundingReceived: Yup.number()
        .typeError("Total funding received must be a number")
        .required("Total funding received is required"),
    })
    .required();

  //fetch all countries
  useEffect(() => {
    const fetchCountries = async () => {
      setIsLoadingCountry(true);
      await CountriesService.getCountriesList().then((response) => {
        response.onSuccess(() => {
          const countryList = response.getContentOrThrowError();
          setCountryNames(countryList.map((country) => country.name));
          setCountries(countryList);
          setIsLoadingCountry(false);
        });
        response.onError(() => {
          NotificationService.error("Error fetching countries");
        });
      });
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const savedCompanyData = JSON.parse(sessionStorage.getItem("companyInformation"));
    if (savedCompanyData && countries.length > 0) {
      const selectedCountryData = countries.find((country) => country.name === savedCompanyData.country);

      if (selectedCountryData) {
        setSelectedCountry(selectedCountryData);
      }
    }
  }, [countries]);

  useEffect(() => {
    if (selectedCountry) {
      fetchAndSetStates(selectedCountry);
    }
  }, [selectedCountry]);

  const fetchAndSetStates = (selectedCountry: CountryData) => {
    setLoadingStates(true);
    if (selectedCountry) {
      CountriesService.getStatesList(selectedCountry?.iso2)
        .then((apiStates) => {
          if (apiStates.getContentOrThrowError().length == 0) {
            const staticStates = getStatesByCountry(selectedCountry.iso2);
            setStates(staticStates);
            setStatesNames(staticStates.map((state) => state.name));
          } else {
            setStates(apiStates.getContentOrThrowError());
            setStatesNames(apiStates.getContentOrThrowError().map((state) => state.name));
          }
        })
        .finally(() => setLoadingStates(false));
    }
  };

  const submitRef = useRef(null);
  const handleFormSubmit = () => {
    if (submitRef.current) {
      ReactGA.event({
        category: "user",
        action: "SecondStepCompleted",
      });
      submitRef.current(); // Call the submit function
    }
  };

  const previousInfo = () => {
    let CompanyData = JSON.parse(sessionStorage.getItem("companyInformation"));
    return CompanyData;
  };

  return (
    <GenericForm
      noMargin
      disableAlert
      buttonsContainerSxProps={{ display: "none" }}
      submitRef={submitRef}
      validateBeforeClosing
      resolver={yupResolver(schema) as unknown as Resolver<CompanyOnboarding>}
      resetValue={previousInfo()}
      dialog={false}
      onSave={async (instance, submitContext) => {
        const validationResponse = await OnboardingService.validateCompanyName(instance.companyName);

        validationResponse
          .onSuccess(() => {
            if (instance.industry != "Others") {
              instance.otherIndustry = null;
            }
            setCompanyInformation(instance as CompanyOnboarding);

            sessionStorage.setItem("companyInformation", JSON.stringify(instance as CompanyOnboarding));
            handleContinue(instance as CompanyOnboarding);
          })
          .onError((message) => NotificationService.error(message));
      }}
      renderContent={(_, control, useFormHookInstance) => {
        /**
         * TODO: Make thousands values work with masks so we can show separators
         * */
        const ebitda = useFormHookInstance.watch("ebitda") == undefined || useFormHookInstance.watch("ebitda") == null;
        const cashOnHand = useFormHookInstance.watch("cashOnHand") == undefined || useFormHookInstance.watch("cashOnHand") == null;
        const monthlyRevenue =
          useFormHookInstance.watch("monthlyRevenue") == undefined || useFormHookInstance.watch("monthlyRevenue") == null;
        const totalFundingReceived =
          useFormHookInstance.watch("totalFundingReceived") == undefined || useFormHookInstance.watch("totalFundingReceived") == null;
        const industry: string = useFormHookInstance.watch("industry");

        return (
          <Container className="main-container">
            {!addCompany && (
              <>
                <Container className="polygons-container">
                  <LazoPurpleBackground showLogo={true} fullwidth={false} />
                </Container>
                <Container className="background-container">
                  <OboardingStepsComponent step={1} />
                </Container>
              </>
            )}
            <Container
              sx={{
                backgroundColor: addCompany ? `${Colors.WHITE} !important` : Colors.LAZO_PURPLE,
                padding: !isMobile && (addCompany ? `0 0 0 0 !important` : "1rem"),
                width: addCompany ? "100% !important" : "",
              }}
              className="body"
            >
              <Container className="form-container">
                <HeaderOnBoardingComponent step={1} />
                <Text className="title">Company</Text>
                <Container className="form-subcontainer">
                  <Container className="two-field-form-row">
                    <Container className="form-item">
                      <Text className="gray-text">Company Name</Text>
                      <TextFieldInput
                        InputProps={{
                          id: "qa-lazo-company-name",
                        }}
                        propertyName="companyName"
                        control={control}
                        disabled={false}
                        TextFieldProps={{
                          sx: { width: "48%", ...textFieldStyles },
                        }}
                      />
                    </Container>
                    <Container className="form-item">
                      <Text className="gray-text">Legal Name</Text>
                      <TextFieldInput
                        InputProps={{
                          id: "qa-lazo-legal-name",
                        }}
                        propertyName="legalName"
                        control={control}
                        disabled={false}
                        TextFieldProps={{
                          sx: { width: "48%", ...textFieldStyles },
                        }}
                      />
                    </Container>
                  </Container>
                  <Container className="form-item">
                    <Text className="gray-text">Short Company Description</Text>
                    <TextFieldInput
                      InputProps={{
                        id: "qa-lazo-description",
                      }}
                      propertyName="description"
                      control={control}
                      disabled={false}
                      TextFieldProps={{
                        sx: { width: "48%", ...textFieldStyles },
                      }}
                    />
                  </Container>
                  <StyledQuestionForm>Where are your operations located?</StyledQuestionForm>
                  <Container className="form-item">
                    <Text className="gray-text">Country</Text>
                    <Controller
                      name="country"
                      control={control}
                      defaultValue={selectedCountry?.name}
                      rules={{ required: true }}
                      render={({ field }) => {
                        return !isLoadingCountry ? (
                          <LazoSelectInput
                            id="qa-lazo-country"
                            value={field.value}
                            options={countryNames}
                            disabled={loadingStates || isLoadingCountry}
                            FormControlProps={{
                              sx: { margin: 0, ...textFieldStyles },
                            }}
                            onChange={(event) => {
                              const selectedCountryName = event.target.value;
                              const selectedCountryData = countries.find((country) => country.name === selectedCountryName);
                              setSelectedCountry(selectedCountryData);
                              fetchAndSetStates(selectedCountryData);
                              field.onChange(event);
                            }}
                            onBoardingForm
                          />
                        ) : (
                          <Skeleton
                            variant="rectangular"
                            width={isMobile ? 340 : 510}
                            height={60}
                            sx={{ borderRadius: "0.75rem", width: "100%" }}
                          />
                        );
                      }}
                    />
                  </Container>

                  <Container className="two-field-form-row">
                    <Container className="form-item">
                      <Text className="gray-text">Zip Code</Text>
                      <TextFieldInput
                        InputProps={{
                          id: "qa-lazo-zip-code",
                        }}
                        propertyName="zipCode"
                        control={control}
                        disabled={false}
                        TextFieldProps={{
                          sx: { width: "48%", ...textFieldStyles },
                        }}
                      />
                    </Container>
                    <Container className="form-item">
                      <Text className="gray-text">State</Text>
                      {!loadingStates ? (
                        <SelectInput
                          propertyName="state"
                          control={control}
                          FormControlProps={{
                            sx: { margin: 0, ...textFieldStyles },
                          }}
                          SelectProps={{
                            sx: { width: "100%", ...textFieldStyles },
                            id: "qa-lazo-state",
                            MenuProps: {
                              PaperProps: {
                                sx: {
                                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                                  border: "solid 1px rgba(0, 0, 0, 0.1)",
                                  maxHeight: "300px",
                                  overflow: "auto",
                                },
                              },
                            },
                          }}
                          options={statesNames}
                          getOptionLabel={(option) => option.toString()}
                          mapToControllerValue={(o) => o ?? ""}
                          isOptionEqualToValue={(o, v) => o === v}
                          disabled={statesNames.length === 0}
                        />
                      ) : (
                        <Skeleton variant="rectangular" width={200} height={60} sx={{ borderRadius: "0.75rem" }} />
                      )}
                    </Container>
                  </Container>
                  <Container className="form-item">
                    <Text className="gray-text">City</Text>
                    <TextFieldInput
                      InputProps={{
                        id: "qa-lazo-city",
                      }}
                      propertyName="city"
                      control={control}
                      disabled={false}
                      TextFieldProps={{
                        sx: { width: "100%", ...textFieldStyles },
                      }}
                    />
                  </Container>
                  <Container className="form-item">
                    <Text className="gray-text">Address</Text>
                    <TextFieldInput
                      InputProps={{
                        id: "qa-lazo-street-address",
                      }}
                      propertyName="streetAddress"
                      control={control}
                      disabled={false}
                      TextFieldProps={{
                        sx: { width: "100%", ...textFieldStyles },
                      }}
                    />
                  </Container>
                  <StyledQuestionForm>Let us know more about you</StyledQuestionForm>
                  <Container className="two-field-form-row">
                    <Container className="form-item">
                      <Text className="gray-text">Website</Text>
                      <TextFieldInput
                        InputProps={{
                          id: "qa-lazo-url",
                        }}
                        propertyName="url"
                        control={control}
                        disabled={false}
                        TextFieldProps={{
                          sx: { width: "48%", ...textFieldStyles },
                        }}
                      />
                    </Container>
                    <Container className="form-item">
                      <Text className="gray-text">Telephone</Text>
                      <TextFieldInput
                        InputProps={{
                          id: "qa-lazo-telephone",
                        }}
                        propertyName="telephone"
                        control={control}
                        disabled={false}
                        TextFieldProps={{
                          sx: { width: "48%", ...textFieldStyles },
                        }}
                      />
                    </Container>
                  </Container>
                  <Container className="form-item">
                    <Text className="gray-text">Industry</Text>

                    <SelectInput
                      propertyName="industry"
                      control={control}
                      FormControlProps={{
                        sx: { ...textFieldStyles, margin: 0 },
                      }}
                      SelectProps={{
                        sx: { ...textFieldStyles },
                        id: "qa-lazo-startup-stage",

                        MenuProps: {
                          PaperProps: {
                            sx: {
                              border: `1px solid ${Colors.LIGHT_GRAY_CDCDCD}`,
                            },
                          },
                        },
                      }}
                      options={INDUSTRIES}
                      getOptionLabel={(option) => option}
                      mapToControllerValue={(o) => o ?? ""}
                      isOptionEqualToValue={(o, v) => o === v}
                    />
                  </Container>

                  <Container className="form-item">
                    {industry && industry == "Others" && (
                      <TextFieldInput
                        InputProps={{ id: "qa-lazo-industry" }}
                        propertyName="otherIndustry"
                        control={control}
                        disabled={false}
                        TextFieldProps={{
                          sx: { width: "48%", ...textFieldStyles },
                        }}
                      />
                    )}
                  </Container>

                  <Container className="form-item">
                    <OnboardingCheckbox
                      label={<Text className="checkbox-text">Delaware C-Corp</Text>}
                      propertyName="delawareCCorp"
                      control={control as unknown as Control<FieldValues>}
                    />
                  </Container>

                  <Container className="two-field-form-row">
                    <Container className="form-item">
                      <Text className="gray-text">Latest Financial Stage</Text>
                      <SelectInput
                        propertyName="startupStage"
                        control={control}
                        FormControlProps={{
                          sx: { ...textFieldStyles, margin: 0 },
                        }}
                        SelectProps={{
                          sx: { ...textFieldStyles },
                          id: "qa-lazo-startup-stage",
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                border: `1px solid ${Colors.LIGHT_GRAY_CDCDCD}`,
                              },
                            },
                          },
                        }}
                        options={FINAL_CATEGORIES.sort()}
                        getOptionLabel={(option) => option}
                        mapToControllerValue={(o) => o ?? ""}
                        isOptionEqualToValue={(o, v) => o === v}
                      />
                    </Container>
                    <Container className="form-item">
                      <Text className="gray-text">Year Founded</Text>
                      <SelectInput
                        propertyName="yearFounded"
                        control={control}
                        FormControlProps={{
                          sx: { ...textFieldStyles, margin: 0 },
                        }}
                        SelectProps={{
                          sx: { ...textFieldStyles },
                          id: "qa-lazo-year-founded",
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                border: `1px solid ${Colors.LIGHT_GRAY_CDCDCD}`,
                              },
                            },
                          },
                        }}
                        options={YEARS}
                        getOptionLabel={(option) => option.toString()}
                        mapToControllerValue={(o) => o ?? 0}
                        isOptionEqualToValue={(o, v) => o === v}
                      />
                    </Container>
                  </Container>

                  <Container className="two-field-form-row">
                    <Container className="form-item">
                      <Text className="gray-text">Number of Employees</Text>
                      <SelectInput
                        propertyName="numberOfEmployees"
                        control={control}
                        FormControlProps={{
                          sx: { ...textFieldStyles, margin: 0 },
                        }}
                        SelectProps={{
                          sx: { ...textFieldStyles },
                          id: "qa-lazo-number-of-employees",
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                border: `1px solid ${Colors.LIGHT_GRAY_CDCDCD}`,
                              },
                            },
                          },
                        }}
                        options={NUMBER_OF_EMPLOYESS}
                        getOptionLabel={(option) => option.toString()}
                        mapToControllerValue={(o) => o ?? ""}
                        isOptionEqualToValue={(o, v) => o === v}
                      />
                    </Container>
                    <Container className="form-item">
                      <Text className="gray-text">Key Investors</Text>
                      <TextFieldInput
                        InputProps={{
                          id: "qa-lazo-key-investors",
                        }}
                        propertyName="keyInvestors"
                        control={control}
                        disabled={false}
                        TextFieldProps={{
                          sx: { width: "48%", ...textFieldStyles },
                        }}
                      />
                    </Container>
                  </Container>

                  <Container className="two-field-form-row-responsive">
                    <Container className="form-item">
                      <Text className="gray-text">EBITDA</Text>
                      <TextFieldInput
                        propertyName="ebitda"
                        control={control}
                        disabled={false}
                        InputProps={{
                          id: "qa-lazo-ebitda",
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{
                                color: ebitda ? Colors.LIGHT_GRAY_8A8E93 : Colors.BLACK_151517,
                              }}
                            >
                              USD
                            </InputAdornment>
                          ),
                        }}
                        TextFieldProps={{
                          sx: { width: "48%", ...textFieldStyles },
                          inputProps: {
                            type: "number",
                            min: "0",
                          },
                        }}
                      />
                    </Container>
                    <Container className="form-item">
                      <Text className="gray-text">Cash on Hand</Text>
                      <TextFieldInput
                        propertyName="cashOnHand"
                        control={control}
                        disabled={false}
                        InputProps={{
                          id: "qa-lazo-cash-on-hand",
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{
                                color: cashOnHand ? Colors.LIGHT_GRAY_8A8E93 : Colors.BLACK_151517,
                              }}
                            >
                              USD
                            </InputAdornment>
                          ),
                        }}
                        TextFieldProps={{
                          sx: { width: "48%", ...textFieldStyles },
                          inputProps: {
                            type: "number",
                            min: "0",
                          },
                        }}
                      />
                    </Container>
                  </Container>

                  <Container className="two-field-form-row-responsive">
                    <Container className="form-item">
                      <Text className="gray-text">Monthly Revenue</Text>
                      <TextFieldInput
                        propertyName="monthlyRevenue"
                        control={control}
                        disabled={false}
                        InputProps={{
                          id: "qa-lazo-monthly-revenue",
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{
                                color: monthlyRevenue ? Colors.LIGHT_GRAY_8A8E93 : Colors.BLACK_151517,
                              }}
                            >
                              USD
                            </InputAdornment>
                          ),
                        }}
                        TextFieldProps={{
                          sx: { width: "48%", ...textFieldStyles },
                          inputProps: {
                            type: "number",
                            min: "0",
                          },
                        }}
                      />
                    </Container>
                    <Container className="form-item">
                      <Text className="gray-text">Total Founding Receipt</Text>
                      <TextFieldInput
                        propertyName="totalFundingReceived"
                        control={control}
                        disabled={false}
                        InputProps={{
                          id: "qa-lazo-total-funding-received",
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{
                                color: totalFundingReceived ? Colors.LIGHT_GRAY_8A8E93 : Colors.BLACK_151517,
                              }}
                            >
                              USD
                            </InputAdornment>
                          ),
                        }}
                        TextFieldProps={{
                          sx: { width: "48%", ...textFieldStyles },
                          inputProps: {
                            type: "number",
                            min: "0",
                          },
                        }}
                      />
                    </Container>
                  </Container>
                </Container>
                <Container></Container>
                <Container
                  sx={{
                    width: !isMobile ? "70%" : "100%",
                    justifyContent: "flex-end",
                    paddingBottom: "1rem",
                  }}
                >
                  <Container
                    sx={{
                      height: "16px",
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "56px",
                      paddingTop: isMobile ? 0 : "48px",
                    }}
                  >
                    {!isMobile && (
                      <>
                        <Button
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textTransform: "none",
                            fontWeight: 500,
                          }}
                          startIcon={<ArrowBackIosNewIcon />}
                          onClick={() => {
                            navigate("/personal-information" + `${getFormattedUtmParams()}`);
                          }}
                        >
                          Back to Personal
                        </Button>

                        <ContinueButton
                          buttonColor={buttonColor ? buttonColor : Colors.LAZO_PURPLE}
                          hideIcon={true}
                          handleContinue={handleFormSubmit}
                        />
                      </>
                    )}

                    {isMobile && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                          height: "100%",
                          alignItems: "center",
                          justifyContent: "space-between",
                          zIndex: 0,
                          position: "relative",
                        }}
                      >
                        <StyledImage className="sign-up-info" src={SignUpQuestionLogo.src} />
                        <Divider
                          sx={{
                            width: "100%",
                            marginBottom: "1rem",
                            marginTop: "32px  ",
                          }}
                        />
                        <ContinueButton
                          buttonColor={buttonColor ? buttonColor : Colors.LAZO_PURPLE}
                          hideIcon={true}
                          handleContinue={handleFormSubmit}
                        />
                      </div>
                    )}
                  </Container>
                  <Container className="white-container" />
                </Container>
              </Container>
            </Container>
            {!isMobile && <StyledImage className="sign-up-info" src={SignUpQuestionLogo.src} />}
          </Container>
        );
      }}
    />
  );
}

const StyledQuestionForm = styled(Typography)({
  fontSize: "14px",
  lineHeight: "20px",
  fontWeight: 600,
  marginTop: "calc(-1rem + 32px)",
  marginBottom: "calc(-1rem + 16px)",
});
